import { useCallback, useMemo } from "react";

import { useExportsApiService } from "shared/services";

import useToast from "./useToast";

import { useDataExportsStore } from "../contexts";
import type { BackendEntityType, EntityRoles, PaginationParamsModel } from "../types";

const useExports = () => {
	const exportsService = useExportsApiService();

	const store = useDataExportsStore();
	const { setState, setInitial } = useDataExportsStore();

	const { showToast } = useToast();

	const methods = useMemo(() => {
		return {
			listExportedData: async (params: PaginationParamsModel) => {
				try {
					setState({ loading: true });
					const { dataExports, nextToken } = await exportsService.listExportedData(params);

					setState(ctx => ({
						nextToken,
						dataExports: !!params?.after ? ctx.dataExports : dataExports
					}));

					return {
						dataExports,
						nextToken
					};
				} catch (err) {
					console.error(err);
					showToast({ text: "Can't list of exported data", noIcon: true });
					return {
						dataExports: [],
						nextToken: null
					};
				} finally {
					setState({ loading: false });
				}
			},
			exportData: async (params: {
				entityId: string;
				entityType: BackendEntityType;
				dataType: BackendEntityType | EntityRoles;
				exportFormat?: "csv" | "pdf" | "xlsx";
			}) => {
				try {
					setState({ exporting: true });
					const { success } = await exportsService.exportData(params);
					showToast({ text: "Data export is being processed and will be generated shortly", noIcon: true });
					return success;
				} catch (err) {
					console.error(err);
					showToast({ text: "Can't export data", noIcon: true });
					return false;
				} finally {
					setState({ exporting: false });
				}
			},
			resetStore() {
				setInitial();
			}
		};
	}, [exportsService, setInitial, setState, showToast]);

	const getData = useCallback(() => {
		return store;
	}, [store]);

	return { ...methods, getData };
};

export default useExports;
